import React from "react";
import {observer} from "mobx-react";
import {useLocation, useNavigate} from "react-router-dom";
import {MenuItem} from "../menu-items";
import AppSidebarCollapsible from "../app-sidebar-collapsible/app-sidebar-collapsible";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import MenuItemContents from "./menu-item-contents/menu-item-contents";

type AppSidebarMenuItemsProps = {
    items: MenuItem[];
};

/** this function controls the initial expansion of the collapsible menu items. If the menu item has active sub-items, it should be opened */
const isSubMenuActive = (pathname: string, menuItems: MenuItem[]) => {
    for (const item of menuItems) {
        if (item.to) {
            if (pathname.startsWith(item.to as string)) {
                return true;
            }
        }
        if (Array.isArray(item.items)) {
            if (isSubMenuActive(pathname, item.items)) {
                return true;
            }
        }
    }

    return false;
}


const TRANSLATION_SCOPE = 'dashboard-side-menu';

const AppSidebarMenuItems: React.FC<AppSidebarMenuItemsProps> = observer(({ items }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { getTranslation } = useDashboardTranslation();

    return (
        <>
            {items.map(({ to, items, type, label_text: labelText, label_key: labelKey, mixpanelEventName }, index) => {
                if (!to && !items) {
                    throw new Error(`A menu item should have either 'to' or 'items' attribute`);
                }
                if (!labelText && !labelKey) {
                    throw new Error(`A menu item should have either 'label_text' or 'label_key' attribute`);
                }

                const label = labelKey ? getTranslation(labelKey, TRANSLATION_SCOPE) : labelText as string;

                if (Array.isArray(items)) {
                    const title = <MenuItemContents label={label} to={to} type={type} label_key={labelKey} mixpanelEventName={mixpanelEventName} />;
                    const hasActiveChild = isSubMenuActive(pathname, items);
                    const onToggle = (isExpanded: boolean) => {
                        if (!isExpanded) {
                            return;
                        }
                        if (hasActiveChild) {
                            return;
                        }
                    };

                    const onParentItemClicked = () => {
                        if (items[0]?.to) {
                            navigate(items[0]?.to);
                        }
                    };

                    return (
                        <AppSidebarCollapsible
                            key={index}
                            title={<span onClick={onParentItemClicked}>{title}</span>}
                            isExpanded={hasActiveChild}
                            onToggle={onToggle}
                        >
                            <AppSidebarMenuItems items={items} />
                        </AppSidebarCollapsible>
                    )
                }
                else {
                    return <MenuItemContents label={label} to={to} type={type} key={index} label_key={labelKey} mixpanelEventName={mixpanelEventName} />;
                }
            })}
        </>
    )
});

export default AppSidebarMenuItems;